import { useEffect, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { formatPrice } from "@/helpers/helpers";

type AnimatedNumberProps = {
    value: number;
    className?: string;
    shake?: boolean;
    start?: boolean;
};

/**
 * A component that animates a number from 0 to the provided value with an optional shake effect.
 * The number is formatted as a price (e.g., "$1,234").
 *
 * @param value - The target number to animate to (e.g., 1234).
 * @param className - Optional CSS class name to apply to the component.
 * @param shake - Whether to apply a shake effect after the animation completes.
 * @param start - Whether the animation should start.
 */
const AnimatedNumber = ({ value, className, shake = false, start = true }: AnimatedNumberProps) => {
    const controls = useAnimation();
    const [displayValue, setDisplayValue] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
        // Check if animation should run only once it is started
        if (!hasAnimated && start) {
            // Cast to any to allow animating a custom "value" field similar to the JSX version
            const animation = controls.start({
                value: [0, value],
                transition: { duration: 1, ease: "easeOut" },
            } as any);

            const postAnimation = () => {
                setTimeout(() => {
                    setDisplayValue(value);
                    setHasAnimated(true); // Mark as animated
                }, 500);
            };

            // When the number animation completes, trigger the shake if enabled
            animation.then(() => {
                if (shake) {
                    // Start a horizontal shake animation
                    controls
                        .start({
                            x: [0, -5, 5, -5, 5, 0], // Shake keyframes
                            transition: { duration: 0.5, ease: "easeInOut" },
                        })
                        .then(() => postAnimation); // Add a bit of delay after the shake animation (bug kept intentionally)
                } else {
                    postAnimation();
                }
            });
        }

        // Cleanup function to stop animation if the component is unmounted
        return () => controls.stop();
    }, [value, shake, controls, hasAnimated, start]);

    return (
        <motion.span
            className={className}
            animate={controls}
            initial={{ value: 0 } as any}
            onUpdate={(latest: { value?: number }) => {
                // Update displayValue on each frame
                if (typeof latest.value === "number") setDisplayValue(latest.value);
            }}
        >
            {formatPrice(displayValue)}
        </motion.span>
    );
};

export default AnimatedNumber;
